import { AfterViewInit, Component, ContentChild, DoCheck, Input, OnDestroy, Optional, Self, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgControl } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subject } from 'rxjs/internal/Subject';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';
import { FormControlBase } from '../../common/form-control-base/form-control-base';
import { ListOption } from '../../models/list-option';
import { Deferred } from '../../helpers/deferred';
import { SelectLabelTemplateDirective, SelectLabelTemplateDirectiveContext, SelectOptionTemplateDirective, SelectOptionTemplateDirectiveContext } from './select-templates.directive';


@Component({
    selector: 'waf-select-input',
    templateUrl: './select-input.component.html',
    inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
    standalone: false
})
export class SelectInputComponent extends FormControlBase<any> implements AfterViewInit, DoCheck, OnDestroy {

    private isBrowserAutocompleteTurnedOff = false;

    @Input()
    get required(): boolean | string { return this._required; }

    set required(value: boolean | string) {
        this._required = value != null && value !== false && `${value}` !== 'false';
    }

    @Input()
    allowClear: boolean;

    @Input()
    appendToBody: boolean;

    @Input()
    isAutoSortable: boolean;

    @Input()
    // options: any = [];
    options: ListOption<number | string>[] = [];

    @Input()
    multiple: boolean = false;

    @Input()
    maxSelectedItems: number;

    @Input()
    hideSelected: boolean = true;

    @Input()
    closeOnSelect: boolean = true;

    @Input()
    relatedData: any;

    @Input()
    optionsLoaderFunc: (searchTerm: string, timeout: Promise<any>, relatedData?: any) => Promise<ListOption<number | string>[]>;

    @ViewChild(NgSelectComponent)
    ngSelectComponent: NgSelectComponent;

    @ContentChild(SelectLabelTemplateDirective, { read: TemplateRef })
    labelTemplate: TemplateRef<SelectLabelTemplateDirectiveContext>;

    @ContentChild(SelectOptionTemplateDirective, { read: TemplateRef })
    optionTemplate: TemplateRef<SelectOptionTemplateDirectiveContext>;

    loading: boolean;

    selectedOptions: ListOption<number | string> | ListOption<number | string>[];

    items$: Observable<ListOption<number | string>[]>;

    readonly searchOptions$: Subject<string> = new Subject<string>();

    get clearable() {
        return this.allowClear != undefined ? this.allowClear : (!this._required || this.multiple);
    }

    private _required !: boolean;
    private _itemSubject$: BehaviorSubject<ListOption<number | string>[]>;
    private searchOptionsSubscription: Subscription;
    private readonly controlTimeoutSource: Deferred;
    private searchTimeoutSource: Deferred;

    constructor(@Self() @Optional() protected override ngModel: NgControl) {
        super('waf-select-input', ngModel);
        this.controlTimeoutSource = new Deferred();
        this._itemSubject$ = new BehaviorSubject<ListOption<number | string>[]>([]);
        this.items$ = this._itemSubject$.asObservable();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes.options) {
            this._itemSubject$.next(this.options || []);
        }

        if (changes.optionsLoaderFunc) {

            if (this.searchOptionsSubscription) {
                this.searchOptionsSubscription.unsubscribe();
            }

            if (this.optionsLoaderFunc) {
                this.searchOptionsSubscription = this.searchOptions$.subscribe(x => this.executeLoader(x));
            }
        }

    }

    ngAfterViewInit() {
        this.turnOffBrowserAutocomplete();
    }

    ngDoCheck(): void {
        if (!this.isBrowserAutocompleteTurnedOff && this.ngSelectComponent) {
            this.turnOffBrowserAutocomplete();
        }
    }

    ngOnDestroy(): void {
        this.controlTimeoutSource.resolve();
        if (this.searchOptionsSubscription) {
            this.searchOptionsSubscription.unsubscribe();
        }
    }

    compareWith(a: ListOption<number | string>, b: ListOption<number | string>) {
        return a && b && a.value === b.value;
    }

    onOpen() {
        //WAF, display preload options
        let options: any;

        if (this.hideSelected && !this.multiple && this.selectedOptions) {
            // FIX BUG WITH HIDE SELECTED FOR SINGLE SELECT
            // NEED TO CREATE NEW ARRAY TO FIRE CHANGE FOR items$ THEREFORE NgSelect will execute compareWith

            options = this.options ? [].concat(this.options) : [];
        }
        else {
            options = this.options;
        }

        this._itemSubject$.next(options);
    }

    onSelectedOptionsChange() {

        let value;

        if (this.multiple) {
            value = (this.selectedOptions as ListOption<number | string>[]).map(x => x.value);
        }
        else {
            value = this.selectedOptions ? (this.selectedOptions as ListOption<number | string>).value : null;
        }

        this.value = value;
    }

    /**
     * Method from Value Accessor. Executed automatically when NgModel are changed from outside
     * @param pervVal  a previous value
     * @param newVal  a next value
     */
    override onValueWritten(pervVal: number | string | number[] | string[], newVal: number | string | number[] | string[]) {
        if (this.multiple) {
            if (newVal && (newVal as any[]).length) {
                this.selectedOptions = (newVal as any[]).map(v => {
                    let option = this.options.find(x => x.value === v);

                    if (!option && this.selectedOptions) {
                        option = (this.selectedOptions as ListOption<number | string>[]).find(x => x.value === v);
                    }

                    if (!option) {
                        option = {
                            value: v,
                            displayName: "NO OPTION PROVIDED"
                        } as any;
                    }

                    return option;
                });
            }
            else {
                this.selectedOptions = [];
            }
        }
        else {
            if (newVal != undefined) {
                if (this.selectedOptions && (this.selectedOptions as ListOption<number | string>).value === newVal) {
                    return;
                }

                let option;

                if(this.options){
                    option = this.options.find(x => x.value === newVal);
                }

                if (!option) {
                    option = {
                        value: newVal,
                        displayName: "NO OPTION PROVIDED"
                    } as any;
                }

                this.selectedOptions = option;
            }
            else {
                this.selectedOptions = null;
            }
        }
    }

    private executeLoader(searchTerm: string) {

        if (this.searchTimeoutSource) {
            this.searchTimeoutSource.resolve();
            this.searchTimeoutSource = null;
        }

        if (!searchTerm || searchTerm.length === 0) {
            this._itemSubject$.next(this.options);
            return;
        }

        const searchTimeoutSource = this.controlTimeoutSource.createDeferred();
        this.searchTimeoutSource = searchTimeoutSource;

        const timeout = searchTimeoutSource.promise();

        const promise = this.optionsLoaderFunc(searchTerm, timeout, this.relatedData);

        promise.then(x => this._itemSubject$.next(x), x => {
            if (searchTimeoutSource.isResolved) {
                console.log("cancelled");
            }
            else {
                alert(`error: ${x}`);
            }
        });
    }

    private turnOffBrowserAutocomplete() {
        if (!this.isBrowserAutocompleteTurnedOff && this.ngSelectComponent) {
          if(this.ngSelectComponent['filterInput']){
            this.ngSelectComponent['filterInput'].nativeElement.setAttribute('autocomplete', 'turn-off'); //Angular upgrade could cause a problem
            this.isBrowserAutocompleteTurnedOff = true;
        }
      }
    }
}
